import React, { useState } from 'react';
import { Eye, MapPin, FileText, DollarSign, CreditCard, Building2 } from 'lucide-react';
import ImageCarousel from './ImageCarousel';

function DeliverySummaryList({ deliveries }) {
  const [selectedDelivery, setSelectedDelivery] = useState(null);

  // Sort deliveries by sequence
  const sortedDeliveries = [...deliveries].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));

  const truncateText = (text, maxLength = 12) => {
    if (!text) return '-';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      delivered: { label: 'Entregado', color: 'bg-green-100 text-green-800' },
      'in transit': { label: 'En tránsito', color: 'bg-blue-100 text-blue-800' },
      pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800' },
      cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800' },
    };

    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-bold text-gray-800 font-montserrat">
            Lista de Entregas
          </h2>
          <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {sortedDeliveries.length} entregas
          </span>
        </div>

        <div className="space-y-3">
          {sortedDeliveries.map((delivery) => (
            <div
              key={delivery.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              {/* Header with sequence and status */}
              <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <span className="bg-brand-primary text-white text-xs font-bold px-2 py-1 rounded">
                    #{delivery.sequence || '-'}
                  </span>
                  <span className="text-sm font-medium text-gray-700">
                    ID: {delivery.id}
                  </span>
                </div>
                {getStatusBadge(delivery.status)}
              </div>

              {/* Content */}
              <div className="p-4">
                {/* Recipient Info */}
                <div className="mb-3">
                  <p className="font-medium text-gray-900 mb-1">
                    {delivery.recipient_name}
                  </p>
                  {delivery.notes_driver && (
                    <div className="flex items-start gap-2 text-sm text-gray-600">
                      <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span className="break-words">
                        {truncateText(delivery.notes_driver, 50)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Financial Info Grid */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  {/* Charge Amount */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600 mb-1">Monto a Cobrar</p>
                    <p className="text-sm font-bold text-gray-900">
                      S/ {(delivery.charge || 0).toFixed(2)}
                    </p>
                  </div>

                  {/* Charged Amount */}
                  <div className={`rounded-lg p-3 ${delivery.charged && delivery.charged > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <div className="flex items-center gap-1 mb-1">
                      <DollarSign className="w-3 h-3 text-gray-600" />
                      <p className="text-xs text-gray-600">Cobrado</p>
                    </div>
                    <p className={`text-sm font-bold ${delivery.charged && delivery.charged > 0 ? 'text-green-700' : 'text-gray-900'}`}>
                      S/ {(delivery.charged || 0).toFixed(2)}
                    </p>
                  </div>

                  {/* FastMax Transfer */}
                  {delivery.fastmax_transfer_amount && delivery.fastmax_transfer_amount > 0 && (
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <div className="flex items-center gap-1 mb-1">
                        <CreditCard className="w-3 h-3 text-yellow-700" />
                        <p className="text-xs text-yellow-700">Transfer FM</p>
                      </div>
                      <p className="text-sm font-bold text-yellow-800">
                        S/ {delivery.fastmax_transfer_amount.toFixed(2)}
                      </p>
                    </div>
                  )}

                  {/* Provider Transfer */}
                  {delivery.provider_transfer_amount && delivery.provider_transfer_amount > 0 && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center gap-1 mb-1">
                        <Building2 className="w-3 h-3 text-blue-700" />
                        <p className="text-xs text-blue-700">Transfer Prov</p>
                      </div>
                      <p className="text-sm font-bold text-blue-800">
                        S/ {delivery.provider_transfer_amount.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>

                {/* Action Button */}
                {delivery.image_urls && delivery.image_urls.length > 0 && (
                  <button
                    onClick={() => setSelectedDelivery(delivery)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary/90 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      Ver Fotos ({delivery.image_urls.length})
                    </span>
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Image Carousel Modal */}
      {selectedDelivery && (
        <ImageCarousel
          delivery={selectedDelivery}
          onClose={() => setSelectedDelivery(null)}
        />
      )}
    </>
  );
}

export default DeliverySummaryList;